import React, { useCallback, useEffect, useRef, useState } from 'react';

import LoadFailed from '../shared/load_failed';
import { fetch_month_recalc_status, post_month_recalc_all } from '../api';
import { error_message } from '../format';
import { POLL_MS, should_poll, start_outcome, status_view } from './logic';
import SelectedTeachers, { TeacherRef } from './selected';
import './recalculate.css';

export const SELECTED_RUN_NOTE =
  'Selected teachers are being recalculated; the whole-month run waits until they finish.';

// Recalculate every OPEN teacher-month of a month, then poll recalc_status every 30s until the run ends.
// The shell mounts it only for TEACHER_PAYMENT_SUPER_USER; the server's 403 still shows as a start error.
const RecalculateTab = ({ pay_month, month_label }) => {
  const [status, setStatus] = useState(null);
  const [poll_error, setPollError] = useState('');
  const [start_error, setStartError] = useState('');
  const [start_note, setStartNote] = useState('');
  const [starting, setStarting] = useState(false);
  const [selected_busy, setSelectedBusy] = useState(false);
  const timer = useRef(null);
  // a month change or unmount bumps this; any late answer for the old month is dropped.
  const gen = useRef(0);
  const last_status = useRef(null);

  const clear_timer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  // each read bumps this; only the latest read may set state or schedule, so overlapping reads
  // (Refresh now / Start during a poll) never leave a second timer behind.
  const req = useRef(0);

  const load = useCallback(() => {
    clear_timer();
    const my = gen.current;
    req.current += 1;
    const mine = req.current;
    const fresh = () => my === gen.current && mine === req.current;
    // the timer re-checks the generation when it fires: a month change or unmount cancels it.
    const schedule = () => {
      clear_timer();
      timer.current = setTimeout(() => {
        if (my === gen.current) load();
      }, POLL_MS);
    };
    return fetch_month_recalc_status(pay_month)
      .then((data) => {
        if (!fresh()) return;
        last_status.current = data;
        setStatus(data);
        setPollError('');
        if (should_poll(data)) schedule();
      })
      .catch((e) => {
        if (!fresh()) return;
        // keep the last known status; a run still going keeps being polled.
        setPollError(error_message(e, 'Could not read the recalculation status.'));
        if (should_poll(last_status.current)) schedule();
      });
  }, [pay_month]);

  useEffect(() => {
    gen.current += 1;
    last_status.current = null;
    setStatus(null);
    setPollError('');
    setStartError('');
    setStartNote('');
    load();
    return () => {
      gen.current += 1;
      clear_timer();
    };
  }, [pay_month, load]);

  const start = () => {
    const my = gen.current;
    setStarting(true);
    setStartError('');
    setStartNote('');
    post_month_recalc_all(pay_month)
      .then((data) => {
        if (my !== gen.current) return;
        const out = start_outcome(data);
        if (out.already_running) setStartNote(out.message);
        load();
      })
      .catch((e) => {
        if (my === gen.current)
          setStartError(error_message(e, 'Could not start the recalculation.'));
      })
      .finally(() => {
        if (my === gen.current) setStarting(false);
      });
  };

  const view = status_view(status);
  const running = Boolean(view && view.running);

  return (
    <div className="tp-rc">
      <p className="tp-hint">
        Recalculates every open teacher-month of the month. Closed months are skipped by the server.
        Don&apos;t deploy while a run is going.
      </p>
      <div className="tp-rc-actions">
        <button
          type="button"
          className="tp-btn"
          disabled={starting || running || selected_busy || !pay_month}
          onClick={start}
        >
          {`Recalculate all teachers for ${month_label}`}
        </button>
        <button type="button" className="tp-btn tp-btn-out" onClick={load}>
          Refresh now
        </button>
      </div>
      {selected_busy ? <div className="tp-banner tp-banner-info">{SELECTED_RUN_NOTE}</div> : null}
      {start_error ? (
        <div className="tp-banner tp-banner-error" role="alert">
          {start_error}
        </div>
      ) : null}
      {start_note ? <div className="tp-banner tp-banner-info">{start_note}</div> : null}
      {poll_error ? <LoadFailed text={poll_error} on_retry={load} /> : null}
      {view ? <StatusPanel view={view} /> : null}
      <SelectedTeachers
        pay_month={pay_month}
        month_label={month_label}
        month_running={running}
        on_busy={setSelectedBusy}
      />
    </div>
  );
};

const StatusPanel = ({ view }) => (
  <div className="tp-rc-status" data-testid="tp-rc-status">
    {view.phase === 'running' ? (
      <div className="tp-banner tp-banner-info">
        Recalculating… this page checks again every 30 seconds.
      </div>
    ) : null}
    {view.phase === 'done' ? <div className="tp-banner tp-banner-ok">{view.done_text}</div> : null}
    {view.waiting_note ? <div className="tp-banner tp-banner-warn">{view.waiting_note}</div> : null}
    {view.phase === 'idle' ? (
      <div className="tp-banner tp-banner-info">
        No recalculation run is recorded for this month.
      </div>
    ) : null}
    <dl className="tp-rc-facts">
      <div>
        <dt>Progress</dt>
        <dd>{view.progress_text}</dd>
      </div>
      <div>
        <dt>Started (IST)</dt>
        <dd>{view.started_at || '—'}</dd>
      </div>
      <div>
        <dt>Finished (IST)</dt>
        <dd>{view.finished_at || (view.running ? 'Running' : '—')}</dd>
      </div>
      <div>
        <dt>Recalculated this run</dt>
        <dd>{view.run_recomputed}</dd>
      </div>
    </dl>
    {view.issues.length ? (
      <div className="tp-banner tp-banner-error" role="alert">
        <div className="tp-rc-issues-title">
          {`${view.issues.length} ${view.issues.length === 1 ? 'issue' : 'issues'}: fix it, then Recalculate again.`}
        </div>
        <ul className="tp-rc-issues">
          {view.issues.map((i, n) => (
            // eslint-disable-next-line react/no-array-index-key -- last_error has no teacher
            <li key={`${i.teacher_uuid}-${n}`}>
              {i.teacher_uuid ? (
                <TeacherRef teacher_uuid={i.teacher_uuid} teacher_name={i.teacher_name} />
              ) : null}
              <span>{i.error}</span>
            </li>
          ))}
        </ul>
        {view.truncated ? (
          <div className="tp-rc-trunc">Only the first errors are listed; more teachers failed.</div>
        ) : null}
      </div>
    ) : null}
  </div>
);

export default RecalculateTab;
